import styles from './index.css';

import * as React from 'react';

import {Hooks, debounce} from '@shared/Utils';
import {Filter, LoadMore, Map, Places} from './Components';
import useWithGooglePlaces from './Services/useWithGooglePlaces';
import mapStyles from './Config/mapStyles.json';
import {TApplicationProps, TRootProps, TPlace} from './Definitions';

import {
    BAD_TOELZ_CENTER as coordinates,
    BAD_TOELZ_INITIAL_ZOOM as zoom,
    BAD_TOELZ_INTERSECTION_COORDINATES as intersectionCoordinates,
} from './Config/maps';

const Client = ({placesService, scrollIntoView, allowedPlaceTypes, allPois, filteredPois, filter, label}: TApplicationProps) => {
    const mostOuterBounds = React.useMemo(() => new google.maps.LatLngBounds(...intersectionCoordinates), []);
    const [activePlace, setActivePlace] = React.useState<TPlace|null>(null);
    const {result, countResult, next, hasMoreResults, onUpdateMapBounds, onUpdateKeyword, isLoading, isLoadingDetailResults, isLoadingQueryResults, additionalFilter, onUpdateAdditionalFilter} = useWithGooglePlaces({
        placesService, allPois, filteredPois, fallbackKeywords: Object.keys(allowedPlaceTypes), filter, mostOuterBounds,
    });

    React.useEffect(() => {
        if (activePlace) {
            scrollIntoView();
        }
    }, [activePlace]);

    const slicedPlaces = result.slice(0, countResult);

    return (
        <div className={styles.layout}>
            <div className={styles.places}>
                {
                    label.headline &&
                        <h2>{label.headline}</h2>
                }
                {
                    label.subHeadline &&
                        <p>{label.subHeadline}</p>
                }
                {
                    <div className={styles.results}>
                        {
                            !isLoadingDetailResults && !isLoadingQueryResults && slicedPlaces.length === 0 && (
                                <p><b>Leider konnten keine passenden Ergebnisse gefunden werden.</b></p>
                            )
                        }
                        <Places
                            {...{activePlace, setActivePlace}}
                            places={slicedPlaces}
                        >
                            {
                                hasMoreResults && (
                                    <LoadMore
                                        isChecking={isLoadingQueryResults}
                                        isLoading={isLoadingDetailResults || isLoadingQueryResults}
                                        loadMore={next}
                                    />
                                )
                            }
                        </Places>
                    </div>
                }
            </div>
            <div className={styles.map}>
                <Map
                    config={{coordinates, zoom}}
                    places={slicedPlaces}
                    activePlace={activePlace}
                    setActivePlace={setActivePlace}
                    onChangeBounds={debounce(onUpdateMapBounds, 300)}
                    isUpdating={isLoading}
                    mapStyles={mapStyles as google.maps.MapTypeStyle[]}
                />
            </div>
            <Filter
                onUpdateKeyword={debounce(onUpdateKeyword, 1000)}
                filter={additionalFilter}
                onUpdateFilter={onUpdateAdditionalFilter}
                className={styles.filter}
            />
        </div>
    );
};

export default ({googleApiKey, ...applicationData}: TRootProps) => {
    const {api} = Hooks.useWithGoogleMaps({
        key: googleApiKey,
        libraries: ['places']
    });
    const placesAttrContainer = React.useRef<HTMLDivElement|null>(null);

    return (
        <>
            <div ref={placesAttrContainer}></div>
            {
                (api && placesAttrContainer.current !== null) &&
                    <Client
                        placesService={new google.maps.places.PlacesService(placesAttrContainer.current)}
                        {...applicationData}
                    />
            }
        </>
    );
}
